<template>
  <vk-modal :stuck="true" :show="show" @update:show="v => !v && close()">
    <vk-modal-close @click="close()"></vk-modal-close>
    <vk-modal-title slot="header">{{ providerName }}'ya gönder</vk-modal-title>

    <div class="text-sm">
      <div v-if="relink" class="border border-red-300 rounded p-3 mb-3">
        {{ providerName }} bağlantınızın yenilenmesi gerekiyor.
        <a href="/profile" class="font-semibold">Profil › Bağlı Hesaplar</a>
        sayfasından hesabınızı yeniden bağlayın.
      </div>

      <div class="flex space-x-2 mb-2">
        <button
          type="button"
          class="px-3 py-1 rounded border"
          :class="{ 'bg-gray-100 font-semibold': tab === 'rooms' }"
          @click="tab = 'rooms'"
        >
          Kanallar
        </button>
        <button
          type="button"
          class="px-3 py-1 rounded border"
          :class="{ 'bg-gray-100 font-semibold': tab === 'users' }"
          @click="tab = 'users'"
        >
          Kişiler
        </button>
      </div>

      <div v-if="tab === 'rooms'">
        <input
          v-model="roomFilter"
          class="uk-input uk-form-small mb-2"
          placeholder="Kanal ara"
          type="text"
        />
        <div class="viqqa-pick-list border rounded">
          <div v-if="roomsLoading" class="p-2 text-gray-500">Yükleniyor…</div>
          <div v-else-if="!filteredRooms.length" class="p-2 text-gray-500">
            Kanal bulunamadı
          </div>
          <div
            v-for="room in filteredRooms"
            :key="'r' + room.id"
            class="viqqa-pick-item"
            :class="{ selected: isSelected('room', room.id) }"
            @click="select('room', room.id, roomLabel(room))"
          >
            {{ roomLabel(room) }}
          </div>
        </div>
      </div>

      <div v-else>
        <input
          v-model="userQuery"
          class="uk-input uk-form-small mb-2"
          placeholder="Kişi ara (ad, kullanıcı adı, e-posta)"
          type="text"
          @input="searchUsers()"
        />
        <div class="viqqa-pick-list border rounded">
          <div v-if="usersLoading" class="p-2 text-gray-500">Aranıyor…</div>
          <div v-else-if="!users.length" class="p-2 text-gray-500">
            Kişi bulunamadı
          </div>
          <div
            v-for="u in users"
            :key="'u' + u.id"
            class="viqqa-pick-item"
            :class="{ selected: isSelected('user', u.id) }"
            @click="select('user', u.id, u.displayName || u.username)"
          >
            {{ u.displayName || u.username }}
            <span class="text-gray-400 text-xs">@{{ u.username }}</span>
          </div>
        </div>
      </div>

      <label class="block mt-3 mb-1">Mesaj</label>
      <textarea
        v-model="message"
        class="uk-textarea"
        style="min-height: 120px"
      ></textarea>

      <div class="flex items-center justify-between mt-3">
        <span class="text-xs text-gray-500">
          <template v-if="target">Alıcı: {{ target.label }}</template>
          <template v-else>Bir kanal ya da kişi seçin</template>
        </span>
        <button
          type="button"
          class="bg-tile-500 text-green-50 cursor-pointer hover:text-white px-3 py-2 rounded-lg"
          :disabled="sending || !target || !message.trim()"
          @click="send()"
        >
          {{ sending ? "Gönderiliyor…" : "Gönder" }}
        </button>
      </div>
    </div>
  </vk-modal>
</template>

<script>
import ViqqaService from "../../services/viqqa";

export default {
  name: "viqqa-send-note",
  props: {
    show: Boolean,
    text: String,
    providerName: { type: String, default: "Viqqa" }
  },
  data() {
    return {
      tab: "rooms",
      rooms: [],
      roomsLoaded: false,
      roomsLoading: false,
      roomFilter: "",
      users: [],
      usersLoading: false,
      userQuery: "",
      searchTimer: null,
      target: null,
      message: "",
      sending: false,
      relink: false
    };
  },
  computed: {
    filteredRooms() {
      const f = this.roomFilter.trim().toLocaleLowerCase("tr");
      if (!f) return this.rooms;
      return this.rooms.filter(r =>
        this.roomLabel(r)
          .toLocaleLowerCase("tr")
          .includes(f)
      );
    }
  },
  watch: {
    show: {
      immediate: true,
      handler(val) {
        if (!val) return;
        this.message = this.text || "";
        this.target = null;
        this.relink = false;
        if (!this.roomsLoaded) this.loadRooms();
      }
    }
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    close() {
      this.$emit("update:show", false);
    },
    roomLabel(room) {
      if (room.name) return (room.type === "direct" ? "" : "#") + room.name;
      return "Doğrudan mesaj";
    },
    isSelected(kind, id) {
      return (
        !!this.target && this.target.kind === kind && this.target.id === id
      );
    },
    select(kind, id, label) {
      this.target = { kind, id, label };
    },
    handleError(data, fallback) {
      if (data && data.relink) this.relink = true;
      this.$root.errorMessage((data && data.msg) || fallback);
    },
    loadRooms() {
      this.roomsLoading = true;
      ViqqaService.rooms()
        .then(resp => {
          if (resp.data && resp.data.success) {
            this.rooms = Array.isArray(resp.data.data) ? resp.data.data : [];
            this.roomsLoaded = true;
          } else {
            this.handleError(resp.data, "Kanallar alınamadı");
          }
        })
        .catch(() => this.$root.errorMessage("Kanallar alınamadı"))
        .finally(() => {
          this.roomsLoading = false;
        });
    },
    searchUsers() {
      clearTimeout(this.searchTimer);
      const q = this.userQuery.trim();
      if (!q) {
        this.users = [];
        return;
      }
      this.searchTimer = setTimeout(() => {
        this.usersLoading = true;
        ViqqaService.users(q)
          .then(resp => {
            if (q !== this.userQuery.trim()) return; // eski yanıt
            if (resp.data && resp.data.success) {
              this.users = Array.isArray(resp.data.data) ? resp.data.data : [];
            } else {
              this.handleError(resp.data, "Kişiler alınamadı");
            }
          })
          .catch(() => this.$root.errorMessage("Kişiler alınamadı"))
          .finally(() => {
            this.usersLoading = false;
          });
      }, 300);
    },
    send() {
      if (!this.target || !this.message.trim() || this.sending) return;
      const payload = { text: this.message.trim() };
      if (this.target.kind === "room") payload.room_id = this.target.id;
      else payload.user_id = this.target.id;

      this.sending = true;
      ViqqaService.send(payload)
        .then(resp => {
          if (resp.data && resp.data.success) {
            this.$root.successMessage(
              this.providerName + "'ya gönderildi: " + this.target.label
            );
            this.close();
          } else {
            this.handleError(resp.data, "Mesaj gönderilemedi");
          }
        })
        .catch(() => this.$root.errorMessage("Mesaj gönderilemedi"))
        .finally(() => {
          this.sending = false;
        });
    }
  }
};
</script>

<style>
.viqqa-pick-list {
  max-height: 200px;
  overflow-y: auto;
}

.viqqa-pick-item {
  padding: 6px 8px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
}

.viqqa-pick-item:hover {
  background: #f7f7f7;
}

.viqqa-pick-item.selected {
  background: #e8f5e9;
  font-weight: 600;
}
</style>
